'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var nthHookLoma = require('@narvar/nth-hook-loma');
var index = require('../../providers/ClaimBuilder/index.js');
var SessionContext = require('../../providers/SessionContext.js');

const imageContentTypeToGraphqlContentType = {
  'image/jpeg': 'IMAGE_JPEG',
  'image/jpg': 'IMAGE_JPG',
  'image/png': 'IMAGE_PNG'
};
const imageGraphqlContentTypeToContentType = {
  IMAGE_JPEG: 'image/jpeg',
  IMAGE_JPG: 'image/jpg',
  IMAGE_PNG: 'image/png'
};
const useSubmitClaim = _ref => {
  let {
    useCreateClaimMutation
  } = _ref;
  const {
    retailerMoniker
  } = SessionContext.useSession();
  const [submitClaimStatus, setSubmitClaimStatus] = React.useState({
    loading: false,
    error: false,
    complete: false,
    success: false,
    called: false
  });
  const {
    state: {
      claimEligibilityInfo,
      itemClaims,
      packageClaims,
      claimType,
      otpVerification,
      cartId
    },
    getFullItemDetails
  } = index.useClaimBuilder();
  const {
    localeCountry,
    localeLanguage
  } = SessionContext.useSession();
  const createClaim = useCreateClaimMutation();
  const [getSignedUploadLink] = nthHookLoma.useGetSignedUploadLinkLazyQuery();
  const getCreateClaimItemPhotosMap = async () => {
    if (!claimEligibilityInfo) throw new Error('No claimEligibilityInfo found during claimCreate');
    const {
      email,
      orderNumber
    } = claimEligibilityInfo;
    // Form a mapping of image details: the itemClaimId the photo is associated with, the file for the photo,
    // and the parameters for this photo to be sent to getSignedUploadLink
    const signedUploadLinkParams = Object.keys(itemClaims).map(itemClaimId => {
      const itemClaim = itemClaims[itemClaimId];
      const itemDetails = getFullItemDetails(itemClaim.uid, itemClaim.packageId);
      if (!itemDetails) throw new Error(`No item details found for item: ${itemClaim.uid} during claimCreate`);
      return (itemClaim.photos ?? []).map(_ref2 => {
        let {
          file
        } = _ref2;
        return {
          itemClaimId,
          file,
          photoDetails: {
            contentType: imageContentTypeToGraphqlContentType[file.type],
            email,
            itemId: itemDetails.itemId,
            orderNumber,
            retailerMoniker,
            sku: itemDetails.sku
          }
        };
      });
    }).flat();

    // Get the signed upload links for each photo that needs to be uploaded for the claim
    const signedUploadLinksResult = await getSignedUploadLink({
      variables: {
        photoDetails: signedUploadLinkParams.map(params => params.photoDetails).flat()
      }
    });
    const {
      signedUploadLink
    } = (signedUploadLinksResult === null || signedUploadLinksResult === void 0 ? void 0 : signedUploadLinksResult.data) ?? {};
    if (!signedUploadLink) throw new Error('Error getting signed upload links');

    // Upload each of the photos according to the unique upload link created for each photo.
    await Promise.all(signedUploadLink.map((uploadLink, i) => {
      return fetch(uploadLink.signedUrl, {
        method: 'PUT',
        body: signedUploadLinkParams[i].file,
        headers: {
          'Content-Type': imageGraphqlContentTypeToContentType[uploadLink.contentType]
        }
      });
    }));
    return signedUploadLinkParams.reduce((acc, param, i) => {
      const newPhotoDetail = {
        contentType: signedUploadLink[i].contentType,
        imageKey: signedUploadLink[i].imageKey
      };
      if (acc[param.itemClaimId]) {
        return {
          ...acc,
          [param.itemClaimId]: [...acc[param.itemClaimId], newPhotoDetail]
        };
      }
      return {
        ...acc,
        [param.itemClaimId]: [newPhotoDetail]
      };
    }, {});
  };
  const submitClaim = async _ref3 => {
    let {
      appeasement
    } = _ref3;
    if (!claimEligibilityInfo || !claimType) throw new Error('No claimEligibilityInfo or claimType found during claimCreate');
    const {
      email,
      orderNumber
    } = claimEligibilityInfo;
    setSubmitClaimStatus({
      loading: true,
      error: false,
      complete: false,
      success: false,
      called: true
    });
    if (index.isItemClaimType(claimType)) {
      const hasPhotos = Object.keys(itemClaims).flatMap(itemClaimId => itemClaims[itemClaimId].photos).length > 0;
      let createClaimItemPhotosMap = {};
      try {
        if (hasPhotos) {
          createClaimItemPhotosMap = await getCreateClaimItemPhotosMap();
        }
        await createClaim({
          variables: {
            email,
            orderNumber,
            retailerMoniker,
            claimType,
            appeasement,
            otpVerification,
            cartId,
            items: Object.keys(itemClaims).map(itemClaimId => {
              const itemClaim = itemClaims[itemClaimId];
              return {
                comments: itemClaim.additionalInfo.comment,
                id: itemClaim.uid,
                photos: createClaimItemPhotosMap[itemClaimId],
                quantity: itemClaim.quantity,
                trackingNumber: itemClaim.trackingNumber,
                itemDamagedConfirmation: claimType === index.ClaimType.ItemDamaged ? itemClaim.additionalInfo.confirmationChecked : undefined,
                itemMissingConfirmation: claimType === index.ClaimType.ItemMissing ? itemClaim.additionalInfo.confirmationChecked : undefined
              };
            }),
            locale: `${localeLanguage ?? ''}_${localeCountry ?? ''}`
          }
        });
        setSubmitClaimStatus({
          loading: false,
          error: false,
          complete: true,
          success: true,
          called: true
        });
      } catch (e) {
        setSubmitClaimStatus({
          loading: false,
          error: true,
          complete: true,
          success: false,
          called: true
        });
      }
    } else {
      try {
        // Call createClaim after photos have been uploaded
        await createClaim({
          variables: {
            email,
            orderNumber,
            retailerMoniker,
            claimType,
            appeasement,
            otpVerification,
            cartId,
            shipments: Object.keys(packageClaims).map(packageClaimId => {
              const packageClaim = packageClaims[packageClaimId];
              return {
                comments: packageClaim.additionalInfo.comment,
                packageMissingConfirmation: packageClaim.additionalInfo.confirmationChecked,
                trackingNumber: packageClaim.trackingNumber
              };
            }),
            locale: `${localeLanguage ?? ''}_${localeCountry ?? ''}`
          }
        });
        setSubmitClaimStatus({
          loading: false,
          error: false,
          complete: true,
          success: true,
          called: true
        });
      } catch (e) {
        setSubmitClaimStatus({
          loading: false,
          error: true,
          complete: true,
          success: false,
          called: true
        });
      }
    }
  };
  return [submitClaim, submitClaimStatus];
};

exports["default"] = useSubmitClaim;
